/**
 * Where the Help menu goes so it always stays on screen, wherever the button has been dragged.
 * It opens above the button, lined up with the button's right edge (the button starts at the
 * bottom right), goes below it when there is no room above, and goes beside it when the screen is
 * too short for either (a phone turned sideways). It slides sideways as far as it must to stay
 * inside the screen. Offsets are measured from the button's bottom-right corner, which does not
 * move when the button shrinks.
 */
const GAP = 8;
const EDGE = 8;

export interface Size {
  width: number;
  height: number;
}

export interface MenuPlacement {
  /** Pixels from the menu's right edge to the button's right edge (negative: the menu sticks out to the right). */
  right: number;
  /** Pixels from the menu's bottom edge to the button's bottom edge (negative: the menu is below the button). */
  bottom: number;
}

// When the room is smaller than the menu the top and left edges win, so the first row stays visible.
const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(value, high));

/**
 * `button` is the button's bottom-right corner in window pixels, `buttonSize` how big the button
 * will be once it has settled, `menu` the menu's measured size and `view` the window's size.
 */
export function menuPlacement(
  button: { right: number; bottom: number },
  buttonSize: number,
  menu: Size,
  view: Size,
): MenuPlacement {
  const place = (left: number, top: number): MenuPlacement => ({
    right: button.right - (left + menu.width),
    bottom: button.bottom - (top + menu.height),
  });
  const keepDown = (top: number) => clamp(top, EDGE, view.height - menu.height - EDGE);

  const lineUp = clamp(button.right - menu.width, EDGE, view.width - menu.width - EDGE);
  const above = button.bottom - buttonSize - GAP - menu.height;
  if (above >= EDGE) return place(lineUp, above);

  const below = button.bottom + GAP;
  if (below + menu.height <= view.height - EDGE) return place(lineUp, below);

  const alongside = keepDown(button.bottom - menu.height);
  const toTheLeft = button.right - buttonSize - GAP - menu.width;
  if (toTheLeft >= EDGE) return place(toTheLeft, alongside);
  const toTheRight = button.right + GAP;
  if (toTheRight + menu.width <= view.width - EDGE) return place(toTheRight, alongside);

  // No room anywhere without covering the button: at least keep the menu on screen.
  return place(lineUp, keepDown(above));
}
